import { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Spinner, Tooltip } from '@crazyegginc/hatch';

import { useAudienceSummary } from '../useAudienceSummary';
import { AUDIENCE_MODES, EVERYONE } from '../constants';

export function AudienceSummaryLine({ definition, onClick, disabled, siteId = null, mode = AUDIENCE_MODES.TARGETING }) {
  const { summary, isLoadingLabels } = useAudienceSummary(definition, { siteId, mode });
  const isInteractive = typeof onClick === 'function';
  const Element = isInteractive ? 'button' : 'div';

  const lineRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const updateIsTruncated = useCallback(() => {
    const node = lineRef.current;
    const nextIsTruncated = Boolean(node && node.scrollWidth > node.clientWidth + 1);

    setIsTruncated((currentIsTruncated) => {
      return currentIsTruncated === nextIsTruncated ? currentIsTruncated : nextIsTruncated;
    });
  }, []);

  useEffect(() => {
    updateIsTruncated();

    const node = lineRef.current;

    if (!node || typeof window.ResizeObserver !== 'function') {
      window.addEventListener('resize', updateIsTruncated);

      return () => window.removeEventListener('resize', updateIsTruncated);
    }

    const resizeObserver = new window.ResizeObserver(updateIsTruncated);
    resizeObserver.observe(node);

    return () => resizeObserver.disconnect();
  }, [summary, isLoadingLabels, updateIsTruncated]);

  const lineProps = isInteractive
    ? {
        type: 'button',
        onClick,
        'aria-disabled': disabled,
        tabIndex: disabled ? -1 : undefined,
      }
    : {};

  const line = (
    <Element
      {...lineProps}
      className={classNames(
        'rounded px-2.5 h-[30px] inline-flex max-w-full min-w-0 items-center overflow-hidden text-body-2 text-left',
        'bg-mystic-500 focus-visible:outline-black',
        {
          'cursor-not-allowed': disabled,
          'cursor-pointer': isInteractive && !disabled,
        },
      )}
    >
      <div ref={lineRef} className="min-w-0 flex-1 truncate whitespace-nowrap">
        {isLoadingLabels ? (
          <>
            <Spinner className="h-3.5 w-3.5 text-cadet-blue-500" />
            <span className="sr-only">Loading audience labels</span>
          </>
        ) : summary.length === 0 ? (
          EVERYONE
        ) : (
          summary.map(({ criteria, text }, index) => (
            <span key={`${criteria}-${text}`}>
              {index > 0 ? <span className="mx-1.25">•</span> : null}
              <span className="font-semibold">{criteria}: </span>
              {text}
            </span>
          ))
        )}
      </div>
    </Element>
  );

  return (
    <Tooltip
      containerStyle={{ zIndex: 100000 }}
      show={isTruncated && !isLoadingLabels}
      interactive={true}
      tooltipContent={<AudienceSummaryTooltipContent summary={summary} />}
    >
      {line}
    </Tooltip>
  );
}

function AudienceSummaryTooltipContent({ summary }) {
  if (summary.length === 0) {
    return <div className="max-w-[264px] text-left text-body-5">{EVERYONE}</div>;
  }

  return (
    <div
      className={classNames(
        'max-w-[264px] max-h-[400px] overflow-y-auto  text-left leading-snug text-sm font-normal p-1',
        'scrollbar-thin scrollbar-track-transparent scrollbar-thumb-cadet-blue-500 scrollbar-thumb-rounded',
      )}
    >
      {summary.map(({ criteria, text, rows }) => (
        <div key={`${criteria}-${text}`} className="py-0.5 first:pt-0 last:pb-0">
          {getTooltipRows(text, rows).map(({ comparison, comparisonPlacement, value, text: rowText }, index) => (
            <div key={`${criteria}-${rowText}-${index}`}>
              <div className={index === 0 ? 'font-semibold' : null}>
                {getTooltipRowLabel(criteria, comparison, comparisonPlacement, index)}:
              </div>
              <div className="pl-3.75 whitespace-normal break-words">
                {getTooltipRowValue(comparison, comparisonPlacement, value)}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function getTooltipRowLabel(criteria, comparison, comparisonPlacement, index) {
  const prefix = index === 0 ? criteria : 'AND';

  return comparison && comparisonPlacement !== 'value' ? `${prefix} ${comparison}` : prefix;
}

function getTooltipRowValue(comparison, comparisonPlacement, value) {
  if (!comparison || comparisonPlacement !== 'value') {
    return value;
  }

  return /^[=<>≥≤]$/.test(comparison) ? `${comparison}${value}` : `${comparison} ${value}`;
}

function getTooltipRows(text, rows) {
  return (
    rows ??
    text.split(' AND ').map((part) => ({
      comparison: '',
      value: part,
      text: part,
    }))
  );
}
